import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ThemeProvider, Typography, useTheme, useMediaQuery } from "@mui/material";
import { Button, Input, Tag, Space } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { Link, useParams } from "react-router-dom";
import Carousel from 'react-material-ui-carousel';
import { Image } from "antd";

import Sidebars from "../../Components/Sidebar/sidebar";
import DataTable from "../../Components/DataTable";

import appColors from "../../config/appColors";
import { postRequest } from "../../Api";
import { apiRoutes } from "../../Api/apiRoutes";
import { setLoader } from "../../Redux/Reducers/gernalSlice";
import { getDateString } from "../../config/functions";
import { VEH_IMAGE_URL } from "../../Api/constants"; // Adjust if your image base URL is different

const CustomerRentalReport = () => {
    const { id } = useParams(); // customer_id
    const theme = useTheme();
    const dispatch = useDispatch();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const user = useSelector((state) => state.authReducer.user);

    const [report, setReport] = useState({});
    const [filterInfo, setFilterInfo] = useState({});
    const [overallSummary, setOverallSummary] = useState({});
    const [periodSummary, setPeriodSummary] = useState({});
    const [activeVehicle, setActiveVehicle] = useState(null);
    const [rentedVehicles, setRentedVehicles] = useState([]);
    const [invoices, setInvoices] = useState([]);

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    /* ===================== API ===================== */
    const getCustomerReport = async () => {
        const onSuccess = (res) => {
            setReport(res);
            setFilterInfo(res.filter || {});
            setOverallSummary(res.overall_summary || {});
            setPeriodSummary(res.period_summary || {});
            setActiveVehicle(res.current_active_vehicle || null);
            setRentedVehicles(res.vehicles_rented_in_period || []);
            setInvoices(res.invoices || []);
            dispatch(setLoader(false));
        };

        const onError = () => {
            dispatch(setLoader(false));
        };

        dispatch(setLoader(true));
        await postRequest(
            {
                token: user?.token,
                cust_id: id,
                start_date: startDate || undefined,
                end_date: endDate || undefined,
            },
            apiRoutes.getCustomerRentalReport, // Adjust route name as per your backend
            onSuccess,
            onError
        );
    };

    useEffect(() => {
        getCustomerReport();
    }, []);

    /* ===================== TABLE COLUMNS - INVOICES ===================== */
    const invoiceColumns = [
        {
            title: 'Invoice id',
            dataIndex: 'inv_id',
            key: 'Invoice id',
            render: (text, record) => <Link
                to={{
                    pathname: `/transaction-details/${text}`,
                    state: record,
                }}
            >
                {text}
            </Link>,
        },
        { title: "Vehicle", dataIndex: "vehicle", key: "vehicle" },
        { title: "Amount", dataIndex: "inv_amount", key: "inv_amount" },
        { title: "Paid", dataIndex: "paid_amount", key: "paid_amount" },
        { title: "Remaining", dataIndex: "remaining_amount", key: "remaining_amount" },
        {
            title: "Status",
            render: (_, r) => (
                <Tag color={r.status === "paid" ? "green" : "orange"} style={{ fontWeight: 600 }}>
                    {r.status?.toUpperCase()}
                </Tag>
            ),
        },
        { title: "Due Date", render: (_, r) => getDateString(r.due_date) },
        { title: "Remarks", dataIndex: "remarks", render: (text) => text || "-" },
    ];

    /* ===================== STYLES (Same as VehicleDetails) ===================== */
    const infoContainer = {
        width: 250,
        borderRadius: 10,
        backgroundColor: appColors.white,
        padding: 5,
        paddingBottom: 20,
        marginTop: 10,
        marginRight: 10,
    };

    const infoHeadingContainer = {
        width: "100%",
        backgroundColor: appColors.primaryLight,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        height: 40,
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
    };

    const infoHeading = { fontSize: 12, color: appColors.white, fontFamily: "Poppins", fontWeight: 600 };
    const infoValue = { fontSize: 14, color: appColors.black, fontFamily: "Poppins", fontWeight: 600 };

    const sectionStyle = { backgroundColor: appColors.white, borderRadius: 10, padding: 15, marginTop: 20 };
    const primaryHeader = {
        width: "100%",
        height: 45,
        borderTopRightRadius: 10,
        borderTopLeftRadius: 10,
        backgroundColor: appColors.primary,
        display: "flex",
        alignItems: "center",
        paddingLeft: 15,
        marginBottom: 15,
    };

    /* ===================== UI ===================== */
    return (
        <ThemeProvider theme={theme}>
            <div style={{ display: "flex" }}>
                <Sidebars />

                <div style={{ marginLeft: 20, width: "75%" }}>
                    {/* Header */}
                    <span className={`headertext ${isSmallScreen ? "small-screen" : ""}`}>
                        Customer Rental Report
                    </span>

                    {/* Filter Row */}
                    <div style={{ display: "flex", alignItems: "center", marginTop: 10, gap: 10 }}>
                        <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={{ width: 150 }} />
                        <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={{ width: 150 }} />
                        <Button type="primary" onClick={getCustomerReport}>
                            Apply Filter
                        </Button>
                    </div>

                    {/* Period Info */}
                    <div style={{
                        backgroundColor: appColors.white,
                        padding: "10px 15px",
                        borderRadius: 10,
                        marginTop: 10,
                        fontSize: 13,
                        color: appColors.greyText,
                        fontFamily: "Poppins",
                    }}>
                        Period: {filterInfo.start_date} → {filterInfo.end_date} ({filterInfo.note})
                    </div>

                    {/* Overall Summary */}
                    <div style={{ display: "flex", flexWrap: "wrap", marginTop: 10 }}>
                        <div style={infoContainer}>
                            <div style={infoHeadingContainer}><Typography sx={infoHeading}>Total Agreements</Typography></div>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: 15 }}>
                                <Typography sx={infoValue}>{overallSummary.total_agreements ?? "-"}</Typography>
                            </div>
                        </div>
                        <div style={infoContainer}>
                            <div style={infoHeadingContainer}><Typography sx={infoHeading}>Total Invoice Amount</Typography></div>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: 15 }}>
                                <Typography sx={infoValue}>£{overallSummary.total_invoice_amount?.toLocaleString() ?? "-"}</Typography>
                            </div>
                        </div>
                        <div style={infoContainer}>
                            <div style={infoHeadingContainer}><Typography sx={infoHeading}>Total Paid</Typography></div>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: 15 }}>
                                <Typography sx={infoValue}>£{overallSummary.total_paid_amount?.toLocaleString() ?? "-"}</Typography>
                            </div>
                        </div>
                        <div style={infoContainer}>
                            <div style={infoHeadingContainer}><Typography sx={infoHeading}>Total Remaining</Typography></div>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: 15 }}>
                                <Typography sx={infoValue}>£{overallSummary.total_remaining?.toLocaleString() ?? "-"}</Typography>
                            </div>
                        </div>
                        <div style={infoContainer}>
                            <div style={infoHeadingContainer}><Typography sx={infoHeading}>Total Vehicles Booked</Typography></div>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: 15 }}>
                                <Typography sx={infoValue}>{overallSummary.total_booked_vehicles ?? "-"}</Typography>
                            </div>
                        </div>
                    </div>

                    {/* Current Active Vehicle */}
                    {activeVehicle && (
                        <div style={sectionStyle}>
                            <div style={primaryHeader}>
                                <Typography style={{ color: appColors.white, fontSize: 16, fontWeight: 600 }}>
                                    Current Active Vehicle
                                </Typography>
                            </div>

                            <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                                {/* Images Carousel */}
                                {activeVehicle.images?.length > 0 && (
                                    <div style={{ width: 220, height: 180 }}>
                                        <Carousel indicators={false} autoPlay={false} navButtonsAlwaysVisible>
                                            {activeVehicle.images.map((img, i) => (
                                                <Image
                                                    key={i}
                                                    src={VEH_IMAGE_URL + img}
                                                    style={{ width: "100%", height: 180, objectFit: "cover", borderRadius: 8 }}
                                                />
                                            ))}
                                        </Carousel>
                                    </div>
                                )}

                                {/* Details */}
                                <div style={{ flex: 1, minWidth: 300 }}>
                                    <Typography style={{ fontFamily: "Poppins", fontSize: 15, fontWeight: 600 }}>
                                        {activeVehicle.veh_name} ({activeVehicle.reg_no})
                                    </Typography>
                                    <Typography style={{ fontFamily: "Poppins", fontSize: 13, color: appColors.greyText, marginTop: 8 }}>
                                        <strong>Agreement ID:</strong> {activeVehicle.agreement_id}<br />
                                        <strong>Period:</strong> {getDateString(activeVehicle.start_date)} → {getDateString(activeVehicle.end_date)}<br />
                                        <strong>Total Rent:</strong> £{activeVehicle.total_rent_generated?.toLocaleString()}<br />
                                        <strong>Paid:</strong> £{activeVehicle.total_paid?.toLocaleString()} |
                                        <strong> Remaining:</strong> £{activeVehicle.remaining?.toLocaleString()}
                                    </Typography>

                                    {activeVehicle.agreement_path && (
                                        <Button
                                            type="primary"
                                            icon={<DownloadOutlined />}
                                            href={activeVehicle.agreement_path}
                                            target="_blank"
                                            style={{ marginTop: 12 }}
                                        >
                                            Download Agreement PDF
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Invoices Table */}
                    <div style={sectionStyle}>
                        <div style={primaryHeader}>
                            <Typography style={{ color: appColors.white, fontSize: 16, fontWeight: 600 }}>
                                Invoices ({invoices.length} records)
                            </Typography>
                        </div>

                        <div style={{ overflowX: "auto" }}>
                            <DataTable
                                columns={invoiceColumns}
                                data={invoices}
                                searchColoumns={["vehicle", "inv_id"]}
                                scroll={{ x: "max-content" }}
                                pagination={{ pageSize: 15 }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </ThemeProvider>
    );
};

export default CustomerRentalReport;